import { useState, useEffect } from 'react';
import { VStack, Text, Heading, Box, HStack } from 'native-base';

import EmojiPicker, {
  Theme as EPTheme,
  EmojiClickData,
} from 'emoji-picker-react';

// Redux
import {
  useAppSelector,
  getVibe,
  clearVibe,
  closeModal,
  useAppDispatch,
  useUserJoinedChatMutation,
  openModal,
  useGetCurrentUserQuery,
  getCurrentBounds,
} from '@breakroom-nx/redux';
import { shuffleColor } from '@breakroom-nx/classes';
import { useDispatch } from 'react-redux';
import {
  PrimaryButton,
  TertiaryButton,
  ModalWrapper,
  Vibe,
  TextField,
  LocalComponentError,
} from '../../';
import { InputType, Status } from '@breakroom-nx/helpers';

import { functions } from '@breakroom-nx/apis';
import { httpsCallable } from 'firebase/functions';

export const CreateChat = () => {
  const dispatch = useDispatch();
  const appDispatch = useAppDispatch();
  const usersBounds = useAppSelector(getCurrentBounds);
  const createChat = httpsCallable(functions, 'chat-create');

  const vibe = useAppSelector(getVibe);

  const [roomTitle, setRoomTitle] = useState<null | string>(null);
  const [creating, setCreating] = useState(false);
  const [emoji, setEmoji] = useState('🎉');
  const [showEmojiPicker, setShowEmojiPicker] = useState(false);
  const [randomColor, setRandomColor] = useState<string>();
  const [errorString, setErrorString] = useState<string>();

  const [joinChat, chatJoinState] = useUserJoinedChatMutation();
  const { data: currentUser } = useGetCurrentUserQuery();

  const textUpdated = (text: string) => {
    setRoomTitle(text ?? text);
  };

  const emojiSelected = (emoji: EmojiClickData) => {
    // console.log(`Selected Emoji: ${emoji.emoji}`);
    setEmoji(emoji.emoji);
  };

  async function makeRoom() {
    setCreating(true);
    if (usersBounds === undefined) {
      setErrorString(
        `We were not able to determine a location. Please try again.`
      );
      setCreating(false);
    } else {
      // console.log(`Map center: ${usersBounds}`);
      createChat({
        location: usersBounds,
        title: roomTitle,
        vibe: vibe,
        avatar: emoji,
        theme: randomColor,
        creatorUID: currentUser?.uid,
      })
        .then(async (result) => {
          const roomId = result.data as string;
          joinChat({ user: currentUser, roomId: roomId })
            .then(async () => {
              setCreating(false);
              dispatch(closeModal());
              dispatch(
                openModal({
                  modalType: 'Alert',
                  modalProps: {
                    status: Status.success,
                    title: 'Room successfully created!',
                  },
                })
              );
              appDispatch(clearVibe());
            })
            .catch(() => {
              setErrorString(`There was an error joining the created room`);
            });
        })
        .catch((err) => {
          setCreating(false);
          setErrorString(`There was an error creating the chat room`);
        });
    }
  }

  useEffect(() => {
    // console.log(`Vibes: ${vibes}`);
    setRandomColor(shuffleColor());
  }, []);

  return (
    <ModalWrapper>
      <VStack bg={'surface.100'} p={7} rounded={'2xl'} width={'full'} space={5}>
        <LocalComponentError error={errorString} />
        <VStack space={'5'} pt={'3'}>
          <Heading fontSize={30} lineHeight={34} textAlign={'center'}>
            Create Room
          </Heading>

          <VStack alignItems={'center'} space={10}>
            <Box alignItems={'center'}>
              <Box
                rounded={'full'}
                alignSelf={'center'}
                bg={randomColor}
                alignItems={'center'}
                justifyContent={'center'}
                // p={'25px'}
                // mb={5}
                size={100}
                p={`${100 * 0.25}px`}
              >
                <Text bg={'transparent'} fontSize={40}>
                  {emoji}
                </Text>
              </Box>
            </Box>

            <VStack space={3}>
              <PrimaryButton
                disabled={creating}
                text={showEmojiPicker ? 'Hide Emoji Picker' : 'Select an Emoji'}
                bg={randomColor}
                onPress={() => {
                  setShowEmojiPicker(!showEmojiPicker);
                }}
              />
              <TertiaryButton
                // disabled={creating}
                text={'Shuffle Background!'}
                color={randomColor}
                onPress={() => {
                  setRandomColor(shuffleColor());
                }}
              />
            </VStack>
            {showEmojiPicker ? (
              <EmojiPicker theme={EPTheme.DARK} onEmojiClick={emojiSelected} />
            ) : null}
          </VStack>

          <HStack alignItems={'center'} space={5} justifyContent={'center'}>
            <Vibe text={vibe.text} icon={vibe.icon} key={vibe.text} />
          </HStack>
          <VStack space={5}>
            <HStack>
              <TextField
                disabled={creating}
                type={InputType.Text}
                onBlur={() => {}}
                placeholder="Name your room..."
                onChangeText={textUpdated}
              />
            </HStack>

            <Text
              textAlign={'center'}
              fontSize={16}
              lineHeight={21}
              fontWeight={200}
              color={'surface.600'}
            >
              Create an interesting and fun name for your room! Please be civil
              and respectful.
            </Text>
            <VStack space={5}>
              <PrimaryButton
                disabled={
                  creating || emoji === undefined || roomTitle === undefined
                }
                isLoading={creating}
                text="Create Room"
                onPress={makeRoom}
              />
              <TertiaryButton
                disabled={creating}
                text="Go Back"
                color="surface.500"
                onPress={() => {
                  dispatch(closeModal());
                  dispatch(openModal({ modalType: 'Randomizer' }));
                }}
              />
            </VStack>
          </VStack>
        </VStack>
      </VStack>
    </ModalWrapper>
  );
};
